// Window-level, capture-phase filter for keyboard-wedge ID scans.
// License payloads (AAMVA, always starting with '@') are swallowed everywhere so repeat
// reads from a presentation scanner never reach the rest of the UI. While a consumer is
// registered (age verification overlay), any scanner-speed burst is also captured.

const KEY_GAP_MS = 80;
const IDLE_MS = 250;
const MIN_BURST_KEYS = 4;
const MODIFIER_KEYS = ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'NumLock', 'AltGraph'];

let installed = false;
let consumer = null;
let session = null;
let pending = { raw: '', lastAt: 0 };
const swallowedKeys = new Set();

export function installIdScanGuard() {
  if (installed) return;
  window.addEventListener('keydown', onKeydown, true);
  window.addEventListener('keypress', onKeypress, true);
  window.addEventListener('keyup', onKeyup, true);
  installed = true;
}

// consumer: { onKeyStart(), onScanStart(meta), onScanProgress(raw, meta), onScan(raw, meta) }
// meta: { leaked } — true when the first keys reached the page before the scan was recognized
export function setIdScanConsumer(nextConsumer) {
  consumer = nextConsumer;
  resetPending();
}

export function clearIdScanConsumer(currentConsumer) {
  if (consumer === currentConsumer) consumer = null;
  resetPending();
}

function onKeydown(e) {
  const now = Date.now();

  if (session) {
    if (!session.confirmed && now - session.lastAt >= KEY_GAP_MS) {
      // A lone '@' typed by a person, not a scanner
      abandonSession();
    } else {
      swallow(e);
      session.raw += charFor(e.key);
      session.lastAt = now;
      if (!session.confirmed && session.raw.length >= MIN_BURST_KEYS) {
        session.confirmed = true;
        notifyStart();
      }
      if (session.confirmed) notifyProgress();
      scheduleFinalize();
      return;
    }
  }

  if (MODIFIER_KEYS.includes(e.key)) return;

  if (e.key === '@') {
    swallow(e);
    session = { raw: '@', lastAt: now, confirmed: false, leaked: false };
    resetPending();
    scheduleFinalize();
    return;
  }

  if (!consumer) return;

  const isFast = pending.raw.length > 0 && now - pending.lastAt < KEY_GAP_MS;

  if (e.key === 'Enter') {
    if (isFast && pending.raw.length >= 2) {
      swallow(e);
      session = { raw: `${pending.raw}\n`, lastAt: now, confirmed: true, leaked: true };
      resetPending();
      notifyStart();
      notifyProgress();
      scheduleFinalize();
    } else {
      resetPending();
    }
    return;
  }

  if (e.key.length !== 1) {
    resetPending();
    return;
  }

  if (!isFast) {
    pending.raw = '';
    if (consumer.onKeyStart) consumer.onKeyStart();
  }
  pending.raw += e.key;
  pending.lastAt = now;

  if (pending.raw.length >= MIN_BURST_KEYS) {
    swallow(e);
    session = { raw: pending.raw, lastAt: now, confirmed: true, leaked: true };
    resetPending();
    notifyStart();
    notifyProgress();
    scheduleFinalize();
  }
}

function notifyStart() {
  if (consumer && consumer.onScanStart) consumer.onScanStart({ leaked: session.leaked });
}

function notifyProgress() {
  if (consumer && consumer.onScanProgress) consumer.onScanProgress(session.raw, { leaked: session.leaked });
}

function onKeypress(e) {
  if (session || swallowedKeys.has(keyId(e))) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}

function onKeyup(e) {
  const id = keyId(e);
  if (swallowedKeys.has(id)) {
    e.preventDefault();
    e.stopImmediatePropagation();
    swallowedKeys.delete(id);
  }
}

function swallow(e) {
  e.preventDefault();
  e.stopImmediatePropagation();
  swallowedKeys.add(keyId(e));
}

function keyId(e) {
  return e.code || e.key;
}

function charFor(key) {
  if (key === 'Enter') return '\n';
  if (key === 'Tab') return '\t';
  return key.length === 1 ? key : '';
}

function scheduleFinalize() {
  if (session.timer) clearTimeout(session.timer);
  session.timer = setTimeout(finalizeSession, IDLE_MS);
}

function finalizeSession() {
  const finished = session;
  session = null;
  // Key-ups arrive within a few ms of their key-downs, so none are still outstanding
  swallowedKeys.clear();
  if (!finished) return;
  if (!finished.confirmed) {
    replayText(finished.raw);
    return;
  }
  if (consumer) consumer.onScan(finished.raw, { leaked: finished.leaked });
}

function abandonSession() {
  if (session.timer) clearTimeout(session.timer);
  const raw = session.raw;
  session = null;
  replayText(raw);
}

function replayText(text) {
  const el = document.activeElement;
  if (!el || el.disabled || el.readOnly) return;
  if (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
    document.execCommand('insertText', false, text);
  }
}

function resetPending() {
  pending = { raw: '', lastAt: 0 };
}
